import { AuthError, TOTPError, NotAuthorizedError } from './components/Auth/Error';
import { LogoutTools } from './hooks/useAuth';
import * as Sentry from '@sentry/react';
// @TODO define errors in one file and use them all over the app
export class BackendError extends Error {
    constructor(message) {
        super(message);
    }
}
export class CrmError extends Error {
    constructor(message, code) {
        super(message);
        this.code = code;
    }
}

async function assertAuthenticated(response) {
    if (response.status === 401) {
        throw new AuthError('AuthenticationRequired');
    }
}

async function assertOk(response) {
    if (response.status !== 200) {
        throw new Error('Server error');
    }
}

async function assertNotInMaintenance(response) {
    if (response.status === 503) {
        LogoutTools.clearBrowserStorage();
        LogoutTools.submitBackendLogout();
    }
}

async function assertAuthorized(data) {
    const authError = data.errors?.find(
        ({ message }) => message === 'This action is unauthorized.'
    );
    if (authError !== undefined) {
        throw new NotAuthorizedError('NotAuthorized');
    }
}

async function watchErrorsContent(data, payload) {
    // checking if errors has proper format
    const errors = data.errors?.find((e) => typeof e.extensions === 'undefined' || !e.extensions);
    if (errors !== undefined) {
        Sentry.captureEvent({
            message: 'Captured a malformed graphql response',
            extra: { data: JSON.stringify(data), payload },
        });
    }
}

async function assertTOTPAuthenticated(data) {
    const totpError = data.errors?.find(
        ({ extensions }) => extensions?.category === 'totp_required'
    );
    if (totpError !== undefined) {
        throw new TOTPError(
            totpError.extensions.totp_secret,
            totpError.extensions.totp_qrcode_image
        );
    }
}

async function assertNoError(data) {
    const unexpectedError = data.errors?.find(({ extensions }) =>
        extensions ? !extensions.validation : false
    ); // @todo double check how validation errors sent back
    if (unexpectedError !== undefined) {
        if (unexpectedError.extensions.category === 'CRM_ERROR') {
            throw new CrmError(unexpectedError.message, unexpectedError?.extensions?.code);
        }
        throw new BackendError(unexpectedError.message);
    }
}

/**
 * Executes a GraphQL query or mutation against the API
 * @param {string} query - The GraphQL query/mutation string
 * @param {Object} [variables={}] - Variables for the GraphQL operation
 * @param {Object} [options={}] - Additional options for the request
 * @param {number} [options.timeout=30000] - Request timeout in milliseconds
 * @param {AbortSignal} [options.signal] - AbortController signal for cancellation
 * @returns {Promise<Object>} The parsed GraphQL response
 * @throws {BackendError|AuthError|TOTPError|NotAuthorizedError}
 */
async function fetchGraphQL(query, variables = {}, options = {}) {
    if (typeof query !== 'string' || !query.trim()) {
        throw new Error('GraphQL query is required');
    }

    const { timeout = 30000, signal } = options;

    const access_token = localStorage.getItem('access_token');
    const totpToken = sessionStorage.getItem('totp_token') || '';
    const auth_header = {};

    if (access_token) {
        auth_header.Authorization = `Bearer ${JSON.parse(access_token).access_token}`;
    }

    const apiUrl = process.env.REACT_APP_API_URL;
    const endpoint = (apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl) + '/graphql';

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    try {
        const response = await fetch(endpoint, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'X-TOTP': totpToken,
                ...auth_header,
            },
            body: JSON.stringify({
                query,
                variables,
            }),
            signal: signal || controller.signal,
        });

        clearTimeout(timeoutId);

        await assertNotInMaintenance(response);
        await assertAuthenticated(response);
        await assertOk(response);

        const data = await response.json();

        await watchErrorsContent(data, {
            query,
            variables,
        });

        await assertAuthorized(data);
        await assertTOTPAuthenticated(data);
        await assertNoError(data);

        return data;
    } catch (error) {
        // Pass through known errors
        if (
            error instanceof AuthError ||
            error instanceof TOTPError ||
            error instanceof NotAuthorizedError ||
            error instanceof CrmError ||
            error instanceof BackendError
        ) {
            throw error;
        }

        // For any other unexpected errors
        throw new BackendError();
    } finally {
        clearTimeout(timeoutId);
    }
}

export default fetchGraphQL;
